import { ComponentProps, useCallback, MouseEvent } from "react";

import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

// Base button styles applied to all buttons
const buttonBaseStyles =
  "inline-flex items-center cursor-pointer justify-center gap-2.5 whitespace-nowrap rounded-md text-base font-semibold transition-all focus-visible:outline-dashed focus-visible:outline focus-visible:outline-black [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive";

// Basic variant options
const variants = {
  intent: {
    primary: "",
    secondary: "",
    success: "",
    danger: "",
    warning: "",
  },
  size: {
    sm: "h-8 px-3 py-1.5 text-sm has-[>svg]:px-2 [&_svg:not([class*='size-'])]:size-3.5",
    md: "h-12 px-4 py-2.5 has-[>svg]:px-3",
    lg: "h-14 px-6 py-3 text-lg has-[>svg]:px-4 [&_svg:not([class*='size-'])]:size-5",
    xl: "h-16 px-8 py-4 text-xl has-[>svg]:px-5 [&_svg:not([class*='size-'])]:size-6",
    icon: "h-9 w-9",
  },
  variant: {
    solid: "",
    outline: "border",
    minimal: "bg-transparent",
    glass:
      "relative before:absolute before:inset-0 before:mix-blend-overlay before:rounded-md before:border",
  },
  disabled: {
    true: "cursor-not-allowed focus-visible:outline-none",
    false: "",
  },
};

// Combined button variants
const buttonVariants = cva(buttonBaseStyles, {
  variants,
  compoundVariants: [
    // PRIMARY + SOLID
    {
      intent: "primary",
      variant: "solid",
      disabled: false,
      className:
        "bg-primary-700 border border-primary-800/20 text-white hover:shadow-sm hover:shadow-blue-300 hover:bg-primary-600 active:inset-shadow-sm active:inset-shadow-primary-800/70 active:border-transparent active:inset-ring-1 active:inset-ring-primary-800/40 active:shadow-none focus-visible:bg-primary-600 focus-visible:outline-primary-400",
    },
    {
      intent: "primary",
      variant: "solid",
      disabled: true,
      className: "bg-primary-700/40 text-white/60 shadow-none",
    },

    // PRIMARY + OUTLINE
    {
      intent: "primary",
      variant: "outline",
      disabled: false,
      className:
        "bg-white hover:bg-primary-50 text-primary-700 border-primary-400 hover:border-primary-700/80 active:bg-primary-200 focus-visible:outline-white",
    },
    {
      intent: "primary",
      variant: "outline",
      disabled: true,
      className:
        "bg-white text-primary-700/40 border-primary-400/40 hover:bg-white active:bg-white",
    },

    // PRIMARY + MINIMAL
    {
      intent: "primary",
      variant: "minimal",
      disabled: false,
      className:
        "text-primary hover:bg-primary-400/10 active:bg-primary-400/20 focus-visible:outline-white",
    },
    {
      intent: "primary",
      variant: "minimal",
      disabled: true,
      className: "text-primary/40 hover:bg-transparent active:bg-transparent",
    },

    // PRIMARY + GLASS
    {
      intent: "primary",
      variant: "glass",
      disabled: false,
      className:
        "text-primary-200 before:bg-primary-100/12 before:border-primary-100/60 hover:before:bg-primary-100/24 hover:before:border-primary-100/80 active:before:bg-primary-100/6 focus-visible:outline-white",
    },
    {
      intent: "primary",
      variant: "glass",
      disabled: true,
      className:
        "text-primary-200/40 before:bg-primary-100/6 before:border-primary/30",
    },

    // SECONDARY + SOLID
    {
      intent: "secondary",
      variant: "solid",
      disabled: false,
      className:
        "bg-gray-200 text-gray-800 border border-gray-300/20 hover:border-gray-300/30 hover:shadow-sm hover:shadow-gray-100 hover:bg-gray-100 active:inset-shadow-sm active:inset-shadow-gray-600/10 active:border-transparent active:inset-ring-1 active:inset-ring-gray-500/10 active:shadow-none focus-visible:bg-gray-200/80 focus-visible:outline-white",
    },
    {
      intent: "secondary",
      variant: "solid",
      disabled: true,
      className: "bg-gray-200/40 text-gray-800/40 shadow-none",
    },

    // SECONDARY + OUTLINE
    {
      intent: "secondary",
      variant: "outline",
      disabled: false,
      className:
        "bg-white text-gray-800 border-gray-800/10 hover:bg-gray-100/80 active:bg-gray-200 focus-visible:outline-white",
    },
    {
      intent: "secondary",
      variant: "outline",
      disabled: true,
      className:
        "bg-white text-gray-800/40 border-gray-800/16 hover:bg-white active:bg-white",
    },

    // SECONDARY + MINIMAL
    {
      intent: "secondary",
      variant: "minimal",
      disabled: false,
      className:
        "text-gray-800 hover:bg-gray-200/80 active:bg-gray-200 focus-visible:outline-white",
    },
    {
      intent: "secondary",
      variant: "minimal",
      disabled: true,
      className: "text-gray-800/40 hover:bg-transparent active:bg-transparent",
    },

    // SECONDARY + GLASS
    {
      intent: "secondary",
      variant: "glass",
      disabled: false,
      className:
        "text-white before:bg-white/12 before:border-white/20 hover:before:bg-white/20 hover:before:border-white active:before:bg-white/6 focus-visible:outline-white",
    },
    {
      intent: "secondary",
      variant: "glass",
      disabled: true,
      className: "text-white/40 before:bg-white/6 before:border-white/10",
    },

    // SUCCESS + SOLID
    {
      intent: "success",
      variant: "solid",
      disabled: false,
      className:
        "bg-green-700 border border-green-800/20 text-white hover:shadow-sm hover:shadow-green-300 hover:bg-green-600 active:inset-shadow-sm active:inset-shadow-green-800/40 active:border-transparent active:inset-ring-1 active:inset-ring-green-800/20 active:shadow-none focus-visible:outline-white",
    },
    {
      intent: "success",
      variant: "solid",
      disabled: true,
      className: "bg-green-700/40 text-white/60 shadow-none",
    },

    // SUCCESS + OUTLINE
    {
      intent: "success",
      variant: "outline",
      disabled: false,
      className:
        "bg-white text-green-700 border-green-700/40 hover:bg-green-50 active:bg-green-100 focus-visible:outline-white",
    },
    {
      intent: "success",
      variant: "outline",
      disabled: true,
      className:
        "bg-white text-green-700/40 border-green-700/20 hover:bg-white active:bg-white",
    },

    // SUCCESS + MINIMAL
    {
      intent: "success",
      variant: "minimal",
      disabled: false,
      className:
        "text-green-700 hover:bg-green-100 active:bg-green-200 focus-visible:outline-white",
    },
    {
      intent: "success",
      variant: "minimal",
      disabled: true,
      className: "text-green-700/40 hover:bg-transparent active:bg-transparent",
    },

    // SUCCESS + GLASS
    {
      intent: "success",
      variant: "glass",
      disabled: false,
      className:
        "text-green-200 before:bg-green-300/36 before:border-green-100/30 hover:before:bg-green-300/48 hover:before:border-green-100/40 active:before:bg-green-500/6 focus-visible:outline-white",
    },
    {
      intent: "success",
      variant: "glass",
      disabled: true,
      className:
        "text-green-200/40 before:bg-green-500/6 before:border-green-500/20",
    },

    // DANGER + SOLID
    {
      intent: "danger",
      variant: "solid",
      disabled: false,
      className:
        "bg-red-700  border border-red-800/20 text-white hover:shadow-sm hover:shadow-red-300 hover:bg-red-600 active:inset-shadow-sm active:inset-shadow-red-900/30 active:border-transparent active:inset-ring-1 active:inset-ring-red-900/20 active:shadow-none focus-visible:outline-white",
    },
    {
      intent: "danger",
      variant: "solid",
      disabled: true,
      className: "bg-red-700/40 text-white/60 shadow-none",
    },

    // DANGER + OUTLINE
    {
      intent: "danger",
      variant: "outline",
      disabled: false,
      className:
        "bg-white text-red-700 border-red-700/40 hover:bg-red-50 active:bg-red-100 focus-visible:outline-white",
    },
    {
      intent: "danger",
      variant: "outline",
      disabled: true,
      className:
        "bg-white text-red-700/40 border-red-700/20 hover:bg-white active:bg-white",
    },

    // DANGER + MINIMAL
    {
      intent: "danger",
      variant: "minimal",
      disabled: false,
      className:
        "text-red-700 hover:bg-red-50 active:bg-red-100 focus-visible:outline-white",
    },
    {
      intent: "danger",
      variant: "minimal",
      disabled: true,
      className: "text-red-700/40 hover:bg-transparent active:bg-transparent",
    },

    // DANGER + GLASS
    {
      intent: "danger",
      variant: "glass",
      disabled: false,
      className:
        "text-red-200 before:bg-red-300/36 before:border-red-100/30 hover:before:bg-red-300/48 hover:before:border-red-100/40 active:before:bg-red-500/6 focus-visible:outline-white",
    },
    {
      intent: "danger",
      variant: "glass",
      disabled: true,
      className: "text-red-200/40 before:bg-red-500/6 before:border-red-500/20",
    },

    // WARNING + SOLID
    {
      intent: "warning",
      variant: "solid",
      disabled: false,
      className:
        "bg-yellow-700 border border-yellow-800/40 text-white hover:shadow-sm hover:shadow-yellow-300 hover:bg-yellow-600 active:inset-shadow-sm active:inset-shadow-yellow-900/40 active:border-transparent active:inset-ring-1 active:inset-ring-yellow-900/20 active:shadow-none focus-visible:outline-white",
    },
    {
      intent: "warning",
      variant: "solid",
      disabled: true,
      className: "bg-yellow-700/40 text-white/60 shadow-none",
    },

    // WARNING + OUTLINE
    {
      intent: "warning",
      variant: "outline",
      disabled: false,
      className:
        "bg-white text-yellow-700 border-yellow-700/40 hover:bg-yellow-50 active:bg-yellow-100 focus-visible:outline-white",
    },
    {
      intent: "warning",
      variant: "outline",
      disabled: true,
      className:
        "bg-white text-yellow-700/40 border-yellow-700/20 hover:bg-white active:bg-white",
    },

    // WARNING + MINIMAL
    {
      intent: "warning",
      variant: "minimal",
      disabled: false,
      className:
        "text-yellow-700 hover:bg-yellow-50 active:bg-yellow-100 focus-visible:outline-white",
    },
    {
      intent: "warning",
      variant: "minimal",
      disabled: true,
      className:
        "text-yellow-700/40 hover:bg-transparent active:bg-transparent",
    },

    // WARNING + GLASS
    {
      intent: "warning",
      variant: "glass",
      disabled: false,
      className:
        "text-yellow-200 before:bg-yellow-300/36 before:border-yellow-100/30 hover:before:bg-yellow-300/48 hover:before:border-yellow-100/40 active:before:bg-yellow-500/6 focus-visible:outline-white",
    },
    {
      intent: "warning",
      variant: "glass",
      disabled: true,
      className:
        "text-yellow-200/40 before:bg-yellow-500/6 before:border-yellow-500/20",
    },
  ],
  defaultVariants: {
    intent: "primary",
    variant: "solid",
    size: "md",
    disabled: false,
  },
});

function Button({
  className,
  intent,
  variant,
  size,
  disabled = false,
  asChild = false,
  onClick,
  ...props
}: ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : "button";

  // Intercept the onClick handler
  const handleClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      if (disabled) {
        event.preventDefault();
        return;
      }
      onClick?.(event);
    },
    [disabled, onClick],
  );

  return (
    <Comp
      data-slot="button"
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      onClick={handleClick}
      className={cn(
        buttonVariants({ intent, variant, size, disabled, className }),
      )}
      {...props}
    />
  );
}

export { Button, buttonVariants };
